/**
 * Camera constraint utilities for getUserMedia with progressive fallback.
 *
 * VIZ-699: On iOS, requesting specific width/height causes asymmetric cropping.
 * Use aspectRatio + resizeMode instead to let iOS use native camera framing.
 */

const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

/**
 * Build a progressive list of video constraints, from highest quality to most relaxed.
 * iOS devices get aspect ratio-based constraints to avoid cropping artifacts.
 */
export function buildVideoConstraints(
  facingMode: "user" | "environment",
): MediaTrackConstraints[] {
  if (isIOS) {
    return [
      { facingMode, aspectRatio: { ideal: 16 / 9 }, resizeMode: "none" } as MediaTrackConstraints,
      { facingMode, resizeMode: "none" } as MediaTrackConstraints,
      { facingMode },
    ];
  }
  return [
    { facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } },
    { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
    { facingMode },
  ];
}

/**
 * Build video-only constraints (no audio) for fallback scenarios.
 */
export function buildVideoOnlyConstraints(
  facingMode: "user" | "environment",
): MediaStreamConstraints[] {
  if (isIOS) {
    return [
      { video: { facingMode, resizeMode: "none" } as MediaTrackConstraints, audio: false },
      { video: { facingMode }, audio: false },
    ];
  }
  return [
    { video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
    { video: { facingMode }, audio: false },
  ];
}

export interface CameraStreamResult {
  stream: MediaStream;
  audioFallback: boolean;
}

/**
 * Acquire a camera stream with progressive constraint relaxation.
 * Tries video+audio first, falls back to video-only if mic is denied.
 *
 * @param facingMode - "user" (front) or "environment" (back)
 * @param existingAudioTrack - Reuse an existing audio track instead of requesting new audio
 */
export async function acquireCameraStream(
  facingMode: "user" | "environment",
  existingAudioTrack?: MediaStreamTrack | null,
): Promise<CameraStreamResult> {
  const videoConstraints = buildVideoConstraints(facingMode);
  let stream: MediaStream | null = null;
  let lastError: unknown;
  let audioFallback = false;

  // Try reusing existing audio track first
  if (existingAudioTrack && existingAudioTrack.readyState === "live") {
    for (const videoConst of videoConstraints) {
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: videoConst,
          audio: false,
        });
        stream = new MediaStream([
          videoStream.getVideoTracks()[0],
          existingAudioTrack,
        ]);
        break;
      } catch (err) {
        lastError = err;
        if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
          break;
        }
      }
    }
  }

  // Full request (video + audio)
  if (!stream) {
    for (const videoConst of videoConstraints) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConst,
          audio: true,
        });
        break;
      } catch (err) {
        lastError = err;
        if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
          break;
        }
      }
    }

    // Video-only fallback (mic denied)
    if (!stream && lastError instanceof DOMException && lastError.name === "NotAllowedError") {
      const videoOnlyAttempts = buildVideoOnlyConstraints(facingMode);
      for (const constraints of videoOnlyAttempts) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          audioFallback = true;
          break;
        } catch (err) {
          lastError = err;
          if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
            break;
          }
        }
      }
    }
  }

  if (!stream) {
    throw lastError instanceof Error ? lastError : new Error("Failed to acquire camera stream");
  }

  return { stream, audioFallback };
}
